import { VFC, useCallback, useState, CSSProperties, useContext, useRef } from 'react'
import {
  Body,
  Bold,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalProps
} from '@panwds/react-ui'
import {
  HEADER_TENANT_MAPPING,
  SUBTEXT_TENANT_MAPPING,
  TITLE_TENANT_MAPPING,
  BUTTON_CANCEL,
  BUTTON_SAVE
} from '../../../constants/LabelConstants'
import ScreenLoader from '../../../../common/components/screenLoader/ScreenLoader'
import FederationMappingTable from '../table/FederationMappingTable'
import GlobalContext from '../../../../common/context/GlobalContext'
import _ from 'lodash'
import { ROOT_NODE_ID } from '../../../../tenancy/constants/TenancyConstants'
import IAMService from '../../../services/IAMService'
import type { TableInstance } from '@panwds/react-table'
import type { Row } from 'react-table'
import { ARRAY } from '../../../../common/utils/Utils'
import {
  ERROR_FEDERATION_MAPPING_ACCESS_DENIED,
  ERROR_FEDERATION_MAPPING_ERROR,
} from '../../../../common/constants/LabelConstants'
import StringTemplate from '../../../../common/utils/StringTemplate'

const TenantMappingModal: VFC<ModalProps & {
  closeModal: () => void,
  domain: any,
  fetchData: Function
}> = ({ closeModal, domain, isOpen, fetchData }) => {

  const { treeReducer, iamReducer } = useContext(GlobalContext)
  const { sgTree = [], sgKeyBy, selectedTreeNode } = treeReducer

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<any>('')
  const [tableState, setTableState] = useState<any>(null)
  const tableRef = useRef<TableInstance<any>>()

  const tableInstanceRef = useCallback((node) => {
    tableRef.current = node
    if (node && !_.isEqual(tableState, node.state)) {
      setTableState(node.state)
    }
  }, [tableState])

  const onSubmit = useCallback(async () => {
    setLoading(true)
    try {
      const previousMappedTSGs = iamReducer.federationMappingsMap?.[domain]?.map(federationMapping => federationMapping.tsg_id) || []
      const selectedIDs = new Set(tableRef?.current?.selectedFlatRows?.map((row) => row?.original?.id) || [])
      selectedIDs.delete(ROOT_NODE_ID)
      const rootSelected: Row<any>[] = (ARRAY.clean(tableRef?.current?.selectedFlatRows) as Row<any>[]).filter(row => !selectedIDs.has(row?.original?.parent?.id)) as Row<any>[]
      const operations: Promise<any>[] = []
      //change calculations
      rootSelected.forEach((row: Row<any>) => {
        if (previousMappedTSGs.includes(row?.original?.id)) {
          _.remove(previousMappedTSGs, id => id === row?.original?.id)
        }
        else {
          operations.push(IAMService.federationMapping.create(row?.original?.id, domain))
        }
      })
      previousMappedTSGs.forEach(id => {
        if (sgKeyBy[id]) {
          operations.push(IAMService.federationMapping.delete(id, domain))
        }
      })

      //execute operations
      await Promise.allSettled(operations)
        .then(async results => {
          const errorTSGs = results.filter(result => {
            return result.status === 'rejected'
          })

          if (errorTSGs.length) {
            const firstError = errorTSGs[0]
            if ('reason' in firstError && firstError?.reason?.response?.status < 500) {
              const tsgID = firstError?.reason?.response?.config.tsg_id
              const tsgDisplayName = sgKeyBy[tsgID].display_name
              setError(StringTemplate(ERROR_FEDERATION_MAPPING_ACCESS_DENIED, [tsgDisplayName]))
            }
            else {
              setError(ERROR_FEDERATION_MAPPING_ERROR)
            }
            //scroll to top
            const modalBody = document.querySelector('.tenantModalBody')
            modalBody?.scrollTo(0, 0)
          }
          else {
            await fetchData({ tsg_id: selectedTreeNode?.id, loading: false })
            closeModal()
          }
        })
    }
    catch (error: any) {
      setError(error.message)
      //scroll to top
      const modalBody = document.querySelector('.tenantModalBody')
      modalBody?.scrollTo(0, 0)
    }
    setLoading(false)
  }, [domain, iamReducer.federationMappingsMap, sgKeyBy, fetchData, selectedTreeNode?.id, closeModal])

  return (
    <Modal style={{ top: '50%', '--tw-translate-y': '-50%' } as CSSProperties} size='lg' onClose={closeModal} isOpen={isOpen}>
      {loading && <ScreenLoader appearance='light'/>}
      <ModalHeader title={TITLE_TENANT_MAPPING}/>

      <ModalBody addClassName='tw-flex-1 tw-overflow-y-scroll tw-overflow-x-hidden tenantModalBody'>
        {error && <Bold appearance='semibold' addClassName='tw-text-error'>{error}</Bold>}
        <div>
          <Bold appearance='semibold'>{HEADER_TENANT_MAPPING}</Bold>
          <Body className='tw-flex tw-flex-col tw-gap-1 '>{SUBTEXT_TENANT_MAPPING}</Body>
        </div>
        <FederationMappingTable
          data={sgTree}
          enableRowSelect
          tableInstanceRef={tableInstanceRef}
          tableRef={tableRef}
          domain={domain}
        />
      </ModalBody>

      <ModalFooter style={{ height: '43px' }}>
        <Button
          onClick={closeModal}
          appearance="secondary"
          disabled={loading}
        >{BUTTON_CANCEL}</Button>
        <Button
          onClick={onSubmit}
          appearance='primary'
          disabled={loading}
        >{BUTTON_SAVE}</Button>
      </ModalFooter>
    </Modal>
  )
}
export default TenantMappingModal
